/**
 * Central keyboard shortcut definitions.
 *
 * `display` is the human-readable string shown in tooltips, the command
 * palette, and the help dialog.  The actual key-handling logic lives in
 * ScadViewer.tsx — this file is the single source of truth for what to show.
 *
 * Platform note: we use ⌘ for the Mac meta key; the keyboard handler in
 * ScadViewer accepts both metaKey and ctrlKey so Windows/Linux users press
 * Ctrl instead.
 */

export interface ShortcutDef {
  /** Short display string, e.g. "F5", "⌘S", "⇧G" */
  display: string;
}

/** Map from command id → shortcut display string */
export const SHORTCUTS: Record<string, ShortcutDef> = {
  // ── General ────────────────────────────────────────────────────────────────
  'action.save': { display: '⌘S' },
  'action.recompile': { display: 'F5' },
  'action.screenshot': { display: '⌘⇧S' },
  'action.ai-assistant': { display: '⌘⇧A' },
  'action.layout-code-preview': { display: '⌘1' },
  'action.layout-all': { display: '⌘2' },
  'action.layout-preview-params': { display: '⌘3' },
  'params.download-stl': { display: '⌘D' },
  'params.randomize': { display: '⇧R' },
  'params.reset': { display: '⇧D' },

  // ── Viewer ─────────────────────────────────────────────────────────────────
  'viewer.fit-to-bounds': { display: 'F' },
  'viewer.grid': { display: 'G' },
  'viewer.wireframe': { display: 'W' },
  'viewer.polygonMode': { display: 'P' },
  'viewer.orthographic': { display: 'O' },
  'viewer.presentation': { display: 'R' },
  'viewer.axis': { display: 'A' },
  'viewer.grayscale': { display: '⇧G' },
  'viewer.measurement': { display: '⇧M' },
  'viewer.inspector': { display: 'I' },
  'viewer.excluded': { display: 'E' },
  'viewer.annotations': { display: 'N' },
};

/** Convenience helper: returns the display string for a command id, or undefined */
export function getShortcut(commandId: string): string | undefined {
  return SHORTCUTS[commandId]?.display;
}
